<template>
  <div id="app" class="page-shell">
    <header v-if="showHeader" class="app-header">
      <div class="app-header-inner">
        <router-link to="/" class="brand-link">
          <img
            src="@/assets/logo.svg"
            alt="Orator Pro"
            width="170"
          />
        </router-link>
        <nav class="header-actions" aria-label="Main navigation">
          <router-link to="/" class="header-link">Events</router-link>
          <router-link v-if="canSeeMessages" to="/manage/messages" class="header-link" :class="{ active: $route.path === '/manage/messages' }">
            Messages <span v-if="messageUnreadCount" class="unread-badge" :aria-label="`${messageUnreadCount} unread conversations`">{{ messageUnreadCount }}</span>
          </router-link>
          <button class="logout-button" type="button" @click="signOut">Sign out</button>
        </nav>
      </div>
    </header>
    <component :is="childComponent"></component>
  </div>
</template>

<script>
import { fetchMessageUniversities, logout } from "@/lib/api";
import { canManageMessages } from "@/lib/auth";

export default {
  name: "MainParent",
  props: {
    childComponent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { messageUnreadCount: 0 };
  },
  computed: {
    canSeeMessages() {
      return canManageMessages();
    },
    showHeader() {
      return !this.$route.path.includes("/presenter");
    },
  },
  methods: {
    async refreshMessageCount() {
      if (!this.canSeeMessages) return;
      try {
        const { universities = [] } = await fetchMessageUniversities();
        this.messageUnreadCount = universities.reduce((count, university) => count + (Number(university.unreadCount) || 0), 0);
      } catch (error) {
        // Messages can be unavailable without blocking the event workflow.
      }
    },
    async signOut() {
      try {
        await logout();
      } finally {
        this.$router.push("/login");
      }
    },
  },
  mounted() {
    this.refreshMessageCount();
    window.addEventListener("orator-messages-updated", this.refreshMessageCount);
  },
  beforeUnmount() {
    window.removeEventListener("orator-messages-updated", this.refreshMessageCount);
  },
};
</script>

<style>
body {
  margin: 0;
  background-color: var(--orator-light);
  font-family: var(--orator-font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  color: var(--orator-navy);
}

.page-shell {
  min-height: 100vh;
}

.app-header {
  background: white;
  border-bottom: 1px solid var(--orator-smoke);
  margin-bottom: 1.5rem;
}

.app-header-inner {
  max-width: 1380px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-link {
  display: inline-flex;
  align-items: center;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
}

.header-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.6rem;
  color: var(--orator-arsenic);
  border-radius: 0.375rem;
  text-decoration: none;
  font-weight: 700;
}

.header-link.active {
  background: var(--orator-cloud);
  color: var(--orator-navy);
}

.unread-badge {
  min-width: 1.4rem;
  padding: 0.1rem 0.35rem;
  border-radius: 1rem;
  background: var(--orator-blue);
  color: white;
  text-align: center;
  font-size: 0.75rem;
}

@media (max-width: 520px) {
  .app-header-inner {
    align-items: flex-start;
    gap: 0.75rem;
  }

  .brand-link img {
    width: 125px;
  }
}

.text-primary {
  color: var(--orator-navy);
}

.text-secondary {
  color: var(--orator-graphite);
}

.logout-button {
  border: 1px solid var(--orator-steel);
  background: white;
  border-radius: 0.375rem;
  padding: 0.45rem 0.8rem;
  font-weight: 800;
  color: var(--orator-arsenic);
}
</style>
